// Linking Google, Discord or Steam to the player's account from the kiosk, during their session: the station link
// handoff (ADR-0053, spec/07 §4.6, spec/12 §8, F-KIO-21).
//
// A kiosk has no better-auth session for its player (spec/07 §4.3), and Google refuses OAuth inside the kiosk's
// embedded web view, so a link runs in the station's locked-down sign-in browser (ADR-0051) and is authorised by a
// **link handoff**, not by a session:
//
//   1. The kiosk (kiosk token) asks which providers are on and which the player on this station's active session has
//      linked. `GET /v1/station/connected-accounts`.
//   2. The kiosk creates a link handoff for one provider: a random, single-use secret bound to this station, to the
//      player of its active session and to that session, alive for three minutes.
//      `POST /v1/station/connected-accounts/link-handoffs`.
//   3. The station opens its sign-in browser on the player web's `/station-link?via=<provider>#<secret>`, which at once
//      starts the provider's link flow for exactly that player (`POST /v1/auth/station-link/start`). No browser session
//      is made: the secret authorises one OAuth state that can link one provider to one account, and nothing else.
//   4. The provider sends the browser back to the api's usual callback, which links the account; the kiosk polls
//      `POST /v1/station/connected-accounts/link-handoffs/{handoffId}/collect` and refreshes its list.
//
// The secret is never logged and never in a request path: it travels in request bodies and the URL fragment.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { SignInHandoffIdSchema, SignInHandoffTokenBodySchema, SignInHandoffTokenSchema, StationBrowserSignInProviderSchema, } from "./station-sign-in-handoff.js";
/** How long a link handoff lives, in seconds: the sign-in browser's own deadline. */
export const LINK_HANDOFF_LIFETIME_SECONDS = 180;
/** How often the kiosk asks whether the link has finished, in seconds. */
export const LINK_HANDOFF_POLL_SECONDS = 2;
export const StationConnectedAccountSchema = z
    .object({
    provider: StationBrowserSignInProviderSchema,
    /** Whether the player's account has this provider linked. */
    linked: z.boolean(),
    /** When it was linked, when it is. Nothing else about the provider account is shown. */
    linkedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationConnectedAccount');
export const StationConnectedAccountsSchema = z
    .object({
    /** One row per provider this deployment has switched on, in the order Google, Discord, Steam. */
    providers: z.array(StationConnectedAccountSchema),
    /**
     * Where the player unlinks one: the player web's account page. Unlinking needs a session signed in within ten
     * minutes (spec/07 §2), which a kiosk never has, so the kiosk only names it.
     */
    manageUrl: z.string().url(),
})
    .openapi('StationConnectedAccounts', { description: 'The providers the player on this station’s session has linked.' });
export const CreateStationLinkHandoffSchema = z
    .object({ provider: StationBrowserSignInProviderSchema })
    .strict()
    .openapi('CreateStationLinkHandoff');
export const StationLinkHandoffSchema = z
    .object({
    id: SignInHandoffIdSchema,
    /**
     * The secret the station puts in the fragment of the page it opens (`/station-link?via=<provider>#<token>`, built
     * from its own configuration). Never shown as a code: a link is finished only in this PC's window.
     */
    token: SignInHandoffTokenSchema,
    provider: StationBrowserSignInProviderSchema,
    expiresAt: IsoDateTimeSchema,
    /** How often to collect, in seconds. */
    pollIntervalSeconds: z.number().int().positive(),
})
    .openapi('StationLinkHandoff', { description: 'A link of one provider to the session’s player, for this station, once.' });
export const STATION_LINK_HANDOFF_STATES = ['waiting', 'linked', 'failed', 'expired', 'cancelled'];
/**
 * Why a link did not happen, in words a kiosk can say:
 * `linked-to-another-account` the provider account is already linked to a different Clavian account;
 * `email-not-verified` Discord says the account's email is not verified, so it cannot be linked;
 * `cancelled` the player said no at the provider;
 * `failed` anything else (the round trip broke, the session ended while it ran).
 */
export const STATION_LINK_FAILURES = ['linked-to-another-account', 'email-not-verified', 'cancelled', 'failed'];
export const StationLinkHandoffStatusSchema = z
    .object({
    /**
     * `waiting`, not finished yet; ask again. `linked`, the provider is now linked to the player's account. `failed`,
     * the round trip came back without a link; `failure` says why. `expired`, past its three minutes, the session
     * ended, or unknown (another station's). `cancelled`, the kiosk cancelled it.
     */
    state: z.enum(STATION_LINK_HANDOFF_STATES),
    provider: StationBrowserSignInProviderSchema.optional(),
    /** Present only with `failed`. */
    failure: z.enum(STATION_LINK_FAILURES).optional(),
})
    .openapi('StationLinkHandoffStatus');
export const StationLinkViewSchema = z
    .object({
    provider: StationBrowserSignInProviderSchema,
    venueName: z.string(),
    stationName: z.string(),
    /**
     * Which Clavian account the provider would be linked to, as a person can recognise it but not read in full: the
     * gamer tag, else the email masked (`ab***@g***.com`), else the phone masked.
     */
    account: z.string(),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('StationLinkView', {
    description: 'What a link handoff would do, for the confirm screen a browser other than the PC’s own window shows.',
});
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <kiosk token>` (spec/07 §4.1). A user session is not accepted here. ' +
    'Only for the player of this station’s own active session, never a player the caller names.';
const NO_SESSION = 'This station has no active session with a player.';
export const getStationConnectedAccountsRoute = createRoute({
    method: 'get',
    path: '/v1/station/connected-accounts',
    operationId: 'getStationConnectedAccounts',
    tags: ['identity', 'stations'],
    summary: 'Which providers the player on this station has linked',
    description: `${STATION_AUTHENTICATION} The providers this deployment has on, each linked or not, and where to unlink one.`,
    responses: {
        200: jsonResponse(StationConnectedAccountsSchema, 'The connected accounts.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse(NO_SESSION, 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createStationLinkHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station/connected-accounts/link-handoffs',
    operationId: 'createStationLinkHandoff',
    tags: ['identity', 'stations'],
    summary: 'Start linking a provider to the player on this station',
    description: `${STATION_AUTHENTICATION} Creates a single-use link handoff bound to this station, its active session and that ` +
        'session’s player, alive for three minutes. Six a station per ten minutes. Audited.',
    request: { body: { content: { 'application/json': { schema: CreateStationLinkHandoffSchema } }, required: true } },
    responses: {
        201: jsonResponse(StationLinkHandoffSchema, 'The link handoff.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse(`${NO_SESSION} Or: the provider is not switched on here.`, 'not-found'),
        409: problemResponse('The player already has this provider linked; or the station or its venue cannot be read, so the notice could not name the PC.', 'account-already-linked', 'station-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const collectStationLinkHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station/connected-accounts/link-handoffs/{handoffId}/collect',
    operationId: 'collectStationLinkHandoff',
    tags: ['identity', 'stations'],
    summary: 'Ask whether the link has finished',
    description: `${STATION_AUTHENTICATION} Only the station that created the link handoff gets an answer other than \`expired\`. ` +
        'Polled every `pollIntervalSeconds`; at most 60 collects a minute per station.',
    request: { params: z.object({ handoffId: SignInHandoffIdSchema }) },
    responses: {
        200: jsonResponse(StationLinkHandoffStatusSchema, 'Where the link stands.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelStationLinkHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station/connected-accounts/link-handoffs/{handoffId}/cancel',
    operationId: 'cancelStationLinkHandoff',
    tags: ['identity', 'stations'],
    summary: 'Cancel a link handoff this station created',
    description: `${STATION_AUTHENTICATION} The player chose Cancel, or left the screen. A link not yet finished can never finish ` +
        'after this. Answers the same for one already gone, or another station’s.',
    request: { params: z.object({ handoffId: SignInHandoffIdSchema }) },
    responses: {
        204: { description: 'Cancelled, or nothing to cancel.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const lookUpStationLinkRoute = createRoute({
    method: 'post',
    path: '/v1/station-link/lookup',
    operationId: 'lookUpStationLink',
    tags: ['identity'],
    summary: 'Which account and PC a link handoff would link to',
    description: 'Unauthenticated: the secret is the authority. Answers the provider, the venue and the PC, and the account masked, so ' +
        'a page opened anywhere but the PC’s own window asks "Link Google to ab***@g***.com at PC 07?" before anything starts. ' +
        'Changes nothing. 30 lookups a minute per address.',
    request: { body: { content: { 'application/json': { schema: SignInHandoffTokenBodySchema } }, required: true } },
    responses: {
        200: jsonResponse(StationLinkViewSchema, 'What the link would do.'),
        400: commonProblemResponses[400],
        409: problemResponse('The link is unknown, past its time, cancelled, already used, or its session has ended.', 'sign-in-handoff-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** The routes here, in the order the kiosk meets them (`POST /v1/auth/station-link/start` is in `auth.ts`). */
export const STATION_LINK_HANDOFF_ROUTES = [
    getStationConnectedAccountsRoute,
    createStationLinkHandoffRoute,
    collectStationLinkHandoffRoute,
    cancelStationLinkHandoffRoute,
    lookUpStationLinkRoute,
];
//# sourceMappingURL=station-link-handoff.js.map